import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';

import { ConnectTemplateToCampaignsRequest } from '../models/campaign/connect-template-to-campaigns-request.interface';
import { ConnectTemplateToPhase } from '../models/campaign/connect-template-to-phase.interface';
import { Template } from '../models/template/template.interface';
import {
  ICampaign,
  ICampaignPhase, IConnectTemplateToPhasesRequest,
  ICreatePhaseRequest,
  IUpdatePhaseRequest
} from '../models/campaign/campaign.interface';


@Injectable({
  providedIn: 'root',
})
export class CampaignService implements OnDestroy {

  private _destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
  ) {
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  // host url
  private getHostUrl = () => {
    const url = environment.scheme + environment.host + environment.port + '/';
    return url;
  };

  // ==================== CAMPAIGN ENDPOINTS ====================

  public readonly getCampaignListUrl = `${this.getHostUrl()}api/v${environment.version
  }/Campaign/getCampaignList`;

  public readonly getCampaignListForTemplateUrl = `${this.getHostUrl()}api/v${environment.version
  }/Campaign/getCampaignListForTemplate`;

  public readonly getCampaignUrl = `${this.getHostUrl()}api/v${environment.version
  }/Campaign/getCampaign`;

  public readonly createCampaignUrl = `${this.getHostUrl()}api/v${environment.version
  }/Campaign/createCampaign`;

  public readonly updateCampaignUrl = `${this.getHostUrl()}api/v${environment.version
  }/Campaign/updateCampaign`;

  public readonly deleteCampaignUrl = `${this.getHostUrl()}api/v${environment.version
  }/Campaign/deleteCampaign`;

  public readonly getCampaignTotalCountUrl = `${this.getHostUrl()}api/v${environment.version
  }/Campaign/getCampaignTotalCount`;

  // ==================== PHASE ENDPOINTS ====================

  public readonly createPhaseUrl = (campaignId: string) =>
    `${this.getHostUrl()}api/v${environment.version}/Campaign/${campaignId}/phase`;

  public readonly updatePhaseUrl = (campaignId: string) =>
    `${this.getHostUrl()}api/v${environment.version}/Campaign/${campaignId}/phase`;

  public readonly deletePhaseUrl = (campaignId: string, phaseId: string) =>
    `${this.getHostUrl()}api/v${environment.version}/Campaign/${campaignId}/phase/${phaseId}`;

  public readonly getPhaseUrl = (campaignId: string, phaseId: string) =>
    `${this.getHostUrl()}api/v${environment.version}/Campaign/${campaignId}/phase/${phaseId}`;

  public readonly editTemplateToPhaseConnectionsUrl = `${this.getHostUrl()}api/v${environment.version
  }/Campaign/editTemplateToPhaseConnections`;

  public readonly connectTemplateToPhaseUrl = `${this.getHostUrl()}api/v${environment.version
  }/Campaign/connectTemplateToPhase`;

  // ==================== DEPRECATED ENDPOINTS ====================

  /**
   * @deprecated Use connectTemplateToPhases instead
   */
  public readonly connectTemplateToCampaignsUrl = `${this.getHostUrl()}api/v${environment.version
  }/Campaign/connectTemplateToCampaigns`;

  // ==================== CAMPAIGN METHODS ====================

  getCampaign(campaignId: string): Observable<ICampaign> {
    let params = new HttpParams().set('campaignId', campaignId);
    return this.http.get<ICampaign>(this.getCampaignUrl, {params});
  }

  getCampaignList(data: {
    first: number,
    rows: number,
    search?: string,
  }): Observable<ICampaign[]> {

    let params = new HttpParams()
      .set('offset', data.first.toString())
      .set('limit', data.rows.toString())
      .set('search', data.search?.toString() ?? '');

    return this.http.get<ICampaign[]>(this.getCampaignListUrl, {params});
  }

  createCampaign(createCampaignData: any): Observable<ICampaign> {
    // Support both old format (with channels) and new format (with phases)
    const request: any = {
      campaignId: createCampaignData.campaignId,
      campaignName: createCampaignData.campaignName,
      userInfo: createCampaignData.userInfo,
    };

    // If initial phases are provided, include them
    if (createCampaignData.initialPhases && createCampaignData.initialPhases.length > 0) {
      request.initialPhases = createCampaignData.initialPhases;
    }

    return this.http.post<ICampaign>(this.createCampaignUrl, request);
  }

  updateCampaign(updateCampaignData: ICampaign): Observable<any> {
    const request: any = {
      campaignId: updateCampaignData.campaignId,
      campaignName: updateCampaignData.campaignName,
      userInfo: updateCampaignData.userInfo,
    };

    return this.http.put<any>(this.updateCampaignUrl, request);
  }

  deleteCampaign(campaignName: string): Observable<any> {
    const url = `${this.deleteCampaignUrl}/${campaignName}`;
    return this.http.delete<any>(url);
  }

  getCampaignTotalCount(): Observable<number> {
    // let params = new HttpParams().set('campaignId', campaignId);
    return this.http.get<number>(this.getCampaignTotalCountUrl);
  }

  // ==================== PHASE METHODS ====================

  createPhase(campaignId: string, request: ICreatePhaseRequest): Observable<ICampaignPhase> {
    return this.http.post<ICampaignPhase>(this.createPhaseUrl(campaignId), request);
  }

  updatePhase(campaignId: string, request: IUpdatePhaseRequest): Observable<any> {
    return this.http.put<any>(this.updatePhaseUrl(campaignId), request);
  }

  deletePhase(campaignId: string, phaseId: string): Observable<any> {
    return this.http.delete<any>(this.deletePhaseUrl(campaignId, phaseId));
  }

  getPhase(campaignId: string, phaseId: string): Observable<ICampaignPhase> {
    return this.http.get<ICampaignPhase>(this.getPhaseUrl(campaignId, phaseId));
  }

  connectTemplateToPhase(request: ConnectTemplateToPhase): Observable<Template> {
    return this.http.post<Template>(this.connectTemplateToPhaseUrl, request);
  }

  // ==================== TEMPLATE CONNECTION METHODS ====================

  /**
   * Connect a template to specific phases within a campaign
   */
  editTemplateToPhaseConnections(request: IConnectTemplateToPhasesRequest): Observable<any> {
    return this.http.post<any>(this.editTemplateToPhaseConnectionsUrl, request);
  }

  /**
   * @deprecated Use connectTemplateToPhases instead
   */
  connectTemplateToCampaigns(connectTemplateToCampaignsRequest: ConnectTemplateToCampaignsRequest): Observable<any> {
    return this.http.post<any>(
      this.connectTemplateToCampaignsUrl,
      connectTemplateToCampaignsRequest);
  }

  getCampaignListForTemplate(templateId: string): Observable<any[]> {
    const params = new HttpParams().set('templateId', templateId);
    return this.http.get<any[]>(this.getCampaignListForTemplateUrl, {params});
  }
}
